import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, DeviceMobile, WhatsappLogo, Copy, Package, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, WHATSAPP_NUMBER, PAGO_MOVIL } from "../lib/api";

export default function ThankYou() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const token = params.get("t");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    if (!orderId || !token) {
      setState("error");
      return;
    }
    api.get(`/orders/${orderId}?token=${encodeURIComponent(token)}`)
      .then((res) => { setOrder(res.data); setState("ok"); })
      .catch(() => setState("error"));
  }, [orderId, token]);

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copiado");
  };

  const whatsappComprobante = () => {
    const lines = (order.items || []).map((i) => `• ${i.quantity}x ${i.name}`).join("\n");
    const msg = `¡Hola Ciudad Garaje! 🍺\nAcabo de hacer un pedido con Pago Móvil.\n\nPedido: ${order.reference}\nNombre: ${order.customer_name}\nTotal: ${money(order.amount)}\n\n${lines}\n\nAdjunto el comprobante de pago.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (state === "loading") {
    return (
      <div className="max-w-2xl mx-auto px-5 py-28 text-center">
        <CircleNotch size={48} className="mx-auto text-[#D97736] animate-spin" />
        <p className="text-gray-400 mt-4">Cargando tu pedido...</p>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="max-w-2xl mx-auto px-5 py-28 text-center">
        <h1 className="font-display text-4xl">No encontramos tu pedido</h1>
        <Link to="/catalogo" className="inline-block mt-6 text-[#D97736] uppercase tracking-widest text-sm">Ir al catálogo</Link>
      </div>
    );
  }

  const isPagoMovil = order.method === "pago_movil";

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-16" data-testid="thankyou-page">
      <div className="text-center">
        <CheckCircle size={64} weight="fill" className="mx-auto text-[#25D366]" />
        <h1 className="font-display text-6xl mt-6">¡Gracias, {order.customer_name?.split(" ")[0]}!</h1>
        <p className="text-gray-300 mt-3">Tu pedido <span className="text-[#D97736] font-semibold">{order.reference}</span> quedó registrado. 🍺</p>
      </div>

      {/* Datos de pago móvil */}
      {isPagoMovil && (
        <div className="mt-10 border border-[#D97736]/40 bg-[#141414] p-6" data-testid="pagomovil-data">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 border border-[#D97736] flex items-center justify-center text-[#D97736]"><DeviceMobile size={20} weight="bold" /></div>
            <div>
              <div className="font-display text-2xl leading-none">Realiza tu Pago Móvil</div>
              <div className="overline">Por un total de {money(order.amount)}</div>
            </div>
          </div>
          <div className="space-y-px bg-white/10 border border-white/10">
            {[
              { l: "RIF", v: PAGO_MOVIL.rif },
              { l: "Teléfono", v: PAGO_MOVIL.telefono },
              { l: "Banco", v: PAGO_MOVIL.banco },
              { l: "Correo", v: PAGO_MOVIL.email },
            ].map((r) => (
              <div key={r.l} className="flex items-center justify-between bg-[#0d0d0d] p-3">
                <span className="text-gray-500 text-sm">{r.l}</span>
                <div className="flex items-center gap-3">
                  <span className="text-white font-medium text-sm break-all">{r.v}</span>
                  <button onClick={() => copy(r.v)} className="text-gray-500 hover:text-[#D97736] transition-colors" title="Copiar"><Copy size={16} /></button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-sm mt-4">
            1) Realiza el Pago Móvil por <span className="text-white font-medium">{money(order.amount)}</span>. 2) Envíanos el comprobante por WhatsApp con tu número de pedido <span className="text-[#D97736]">{order.reference}</span>.
          </p>
          <button
            onClick={whatsappComprobante}
            className="w-full mt-5 flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-black font-semibold uppercase tracking-wide py-4 transition-colors"
            data-testid="send-comprobante-btn"
          >
            <WhatsappLogo size={22} weight="fill" /> Enviar comprobante por WhatsApp
          </button>
        </div>
      )}

      {!isPagoMovil && (
        <div className="mt-10 border border-[#25D366]/40 bg-[#141414] p-6 text-center">
          <p className="text-gray-300">Continúa tu pedido por WhatsApp; te confirmaremos disponibilidad y pago.</p>
          <button onClick={whatsappComprobante} className="mt-5 inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-black font-semibold uppercase tracking-wide px-6 py-4 transition-colors">
            <WhatsappLogo size={22} weight="fill" /> Abrir WhatsApp
          </button>
        </div>
      )}

      {/* Detalle */}
      <div className="mt-8 bg-[#171717] border border-white/10 p-6">
        <div className="overline mb-4 flex items-center gap-2"><Package size={16} /> Detalle del pedido</div>
        {(order.items || []).map((it, i) => (
          <div key={it.beer_id || i} className="flex justify-between py-2 border-b border-white/5 text-sm">
            <span>{it.quantity}x {it.name}</span>
            <span>{money(it.price * it.quantity)}</span>
          </div>
        ))}
        <div className="flex justify-between items-end pt-4">
          <span className="overline">Total</span>
          <span className="font-display text-3xl text-[#D97736]">{money(order.amount)}</span>
        </div>
      </div>

      <div className="text-center mt-10">
        <Link to="/catalogo" className="inline-block bg-[#D97736] hover:bg-[#E88D50] text-black font-semibold uppercase tracking-wide px-8 py-4 transition-colors">
          Seguir comprando
        </Link>
      </div>
    </div>
  );
}
